import React, { useState } from "react";
import { Alert, Box, Button, Chip, Divider, Grid, Link, Paper, Stack, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const AWS_READ_POLICY = {
  Version: "2012-10-17",
  Statement: [
    {
      Sid: "InventoryAndCostRead",
      Effect: "Allow",
      Action: [
        "s3:ListAllMyBuckets",
        "ec2:DescribeInstances",
        "ec2:DescribeVolumes",
        "rds:DescribeDBInstances",
        "lambda:ListFunctions",
        "dynamodb:ListTables",
        "dynamodb:DescribeTable",
        "elasticache:DescribeCacheClusters",
        "ce:GetCostAndUsage"
      ],
      Resource: "*"
    },
    {
      Sid: "OptionalBucketDetails",
      Effect: "Allow",
      Action: ["s3:GetBucketLocation", "s3:ListBucket"],
      Resource: "arn:aws:s3:::*"
    }
  ]
};

const GUIDES = {
  aws: {
    name: "Amazon Web Services",
    intro: "Connect a dedicated IAM user to the enterprise inventory. This form uses an access key; the public AWS assessment has a separate role-based setup.",
    collect: ["Access key ID", "Secret access key", "Primary AWS Region"],
    minimum: "s3:ListAllMyBuckets is required for this connector to validate and list buckets. Add the other read actions below for the report sections you want.",
    steps: [
      { title: "Create a dedicated IAM user", text: "In the customer's AWS account, open IAM → Users → Create user. Give it a clear name such as susha-inventory-reader. Do not create or enter root access keys." },
      { title: "Attach read permissions", text: "Open the user's Permissions tab. Create and attach a customer-managed policy using the read-only example below. Review it with the account owner: ListBucket can expose object names; remove that optional statement if bucket object counts are not needed." },
      { title: "Create an access key", text: "Open the user's Security credentials tab → Access keys → Create access key. Choose the use case that matches an application outside AWS. Copy the key ID and secret key when shown; AWS does not show the secret again." },
      { title: "Connect and check coverage", text: "Choose the Region containing the resources you want to inspect, paste both key values, and select Connect. Review each report section. An empty optional section can mean missing IAM permission, not necessarily zero resources." }
    ],
    notes: ["Regional inventory is limited to the selected Region. S3 buckets and Cost Explorer are account-wide.", "This enterprise connector stores an encrypted long-lived IAM key. Rotate it through Update credentials. Never use a root key."],
    links: [
      { label: "AWS: create an IAM user", href: "https://docs.aws.amazon.com/IAM/latest/UserGuide/id_users_create.html" },
      { label: "AWS: manage IAM user access keys", href: "https://docs.aws.amazon.com/IAM/latest/UserGuide/access-keys-admin-managed.html" },
      { label: "AWS: ListBuckets permission", href: "https://docs.aws.amazon.com/AmazonS3/latest/API/API_ListBuckets.html" }
    ],
    policy: AWS_READ_POLICY
  },
  azure: {
    name: "Microsoft Azure",
    intro: "Connect an app registration to list the subscriptions it can read. A tenant ID alone does not give the app access to a subscription.",
    collect: ["Directory (tenant) ID", "Application (client) ID", "Client secret Value"],
    minimum: "Assign the app's service principal the Reader role on at least one Azure subscription. Microsoft Graph API permissions are not required for this Azure connector.",
    steps: [
      { title: "Register an application", text: "In Microsoft Entra admin center, select Entra ID → App registrations → New registration. Use the customer's tenant. A single-tenant registration is sufficient for this form." },
      { title: "Copy the two IDs", text: "On the app's Overview page, copy Application (client) ID and Directory (tenant) ID. The tenant's domain name is not the tenant ID." },
      { title: "Create a client secret", text: "Open Certificates & secrets → Client secrets → New client secret. Choose an expiry and copy the secret Value immediately. Do not paste the Secret ID into the form." },
      { title: "Grant subscription access", text: "Open Subscriptions → choose the subscription → Access control (IAM) → Add role assignment. Assign Reader to the app's service principal. Repeat for each subscription you want visible." },
      { title: "Connect and verify", text: "Paste the three values into the Azure form. The connection succeeds only if the app can list at least one accessible subscription." }
    ],
    notes: ["This connector currently lists accessible subscriptions; it does not collect Azure resource inventory or billing detail.", "When the secret expires, create a new secret and use Update credentials in Cloud Connections."],
    links: [
      { label: "Microsoft: register an app", href: "https://learn.microsoft.com/en-us/entra/identity-platform/quickstart-register-app" },
      { label: "Microsoft: assign a subscription role", href: "https://learn.microsoft.com/en-us/entra/identity-platform/howto-create-service-principal-portal" }
    ]
  },
  m365: {
    name: "Microsoft 365",
    intro: "Connect an app registration to Microsoft Graph for organization, license, and user summaries. This is app-only access; a user does not need to remain signed in to Microsoft 365.",
    collect: ["Directory (tenant) ID", "Application (client) ID", "Client secret Value"],
    minimum: "Grant Microsoft Graph Application permissions Organization.Read.All, LicenseAssignment.Read.All, and User.Read.All, then grant tenant-wide admin consent.",
    steps: [
      { title: "Register an application", text: "In Microsoft Entra admin center, open Entra ID → App registrations → New registration in the customer's tenant. Use a clear name such as susha-m365-reporting." },
      { title: "Copy IDs and create a secret", text: "On Overview copy Application (client) ID and Directory (tenant) ID. Under Certificates & secrets → Client secrets, create a secret and copy its Value immediately, not its Secret ID." },
      { title: "Add Graph application permissions", text: "Open API permissions → Add a permission → Microsoft Graph → Application permissions. Add Organization.Read.All, LicenseAssignment.Read.All, and User.Read.All. Delegated permissions do not satisfy this connector." },
      { title: "Grant admin consent", text: "On the API permissions page select Grant admin consent for the customer's tenant. Confirm all three permissions show Granted. Ask a tenant administrator with the required consent role to do this step." },
      { title: "Connect and verify", text: "Paste the tenant ID, client ID, and secret Value into the Microsoft 365 form. The app checks organization, licenses, and users before saving the connection." }
    ],
    notes: ["The current user count is from the first Microsoft Graph page (up to 1,000 users), so large tenants need pagination before treating it as a full tenant count.", "When the client secret expires, create a new one and use Update credentials in Cloud Connections."],
    links: [
      { label: "Microsoft: Graph app permissions", href: "https://learn.microsoft.com/en-us/entra/identity-platform/quickstart-configure-app-access-web-apis#application-permission-to-microsoft-graph" },
      { label: "Microsoft: license permissions", href: "https://learn.microsoft.com/en-us/graph/api/subscribedsku-list?view=graph-rest-1.0" },
      { label: "Microsoft: user permissions", href: "https://learn.microsoft.com/en-us/graph/api/user-list?view=graph-rest-1.0" }
    ]
  }
};

export const CLOUD_SETUP_PROVIDERS = [
  { id: "aws", name: GUIDES.aws.name },
  { id: "azure", name: GUIDES.azure.name },
  { id: "m365", name: GUIDES.m365.name }
];

export default function CloudSetupGuide({ provider }) {
  const [copied, setCopied] = useState(false);
  const guide = GUIDES[provider];
  if (!guide) return null;

  async function copyPolicy() {
    try {
      await navigator.clipboard.writeText(JSON.stringify(guide.policy, null, 2));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Stack spacing={2.5} sx={{ py: 1 }}>
      <Box>
        <Typography variant="h6" fontWeight={800}>{guide.name} setup</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{guide.intro}</Typography>
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Typography variant="subtitle2" fontWeight={800}>Values to collect</Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
              {guide.collect.map((value) => <Chip key={value} label={value} size="small" variant="outlined" />)}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Typography variant="subtitle2" fontWeight={800}>Minimum access</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{guide.minimum}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Divider />
      <Stack spacing={2}>
        {guide.steps.map((step, index) => (
          <Stack key={step.title} direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "primary.main", color: "white", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{index + 1}</Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={800}>{step.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{step.text}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      {guide.policy && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc" }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1}>
            <Box>
              <Typography variant="subtitle2" fontWeight={800}>Example read-only IAM policy</Typography>
              <Typography variant="caption" color="text.secondary">Review with the account owner before attaching. The second statement is optional bucket detail access.</Typography>
            </Box>
            <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyPolicy}>{copied ? "Copied" : "Copy policy"}</Button>
          </Stack>
          <Box component="pre" sx={{ mt: 1.5, mb: 0, p: 1.5, borderRadius: 1, bgcolor: "#0f172a", color: "#e2e8f0", fontSize: 12, lineHeight: 1.5, overflowX: "auto", maxHeight: 300 }}>
            {JSON.stringify(guide.policy, null, 2)}
          </Box>
        </Paper>
      )}

      <Alert severity="info">
        <Stack spacing={0.5}>{guide.notes.map((note) => <Typography key={note} variant="body2">{note}</Typography>)}</Stack>
      </Alert>
      <Box>
        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>Official provider instructions</Typography>
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          {guide.links.map((link) => <Link key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, fontSize: 13 }}>{link.label}<OpenInNewIcon sx={{ fontSize: 14 }} /></Link>)}
        </Stack>
      </Box>
    </Stack>
  );
}
